import React, { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { GLYPH_STROKES } from "../../../../data/glyphStrokes";
import {
  COUNTING_ENGINE_THEME,
  COUNTING_SPOKEN_WORDS,
  countingTarget,
  countingTraceNumeral,
} from "../../../../data/countingLadderCatalog";
import { CountingPiece, CountingRoot, PIECE_NOUN, POP_SPRING } from "../CountingPiece";
import GlyphTracer from "../../interaction/GlyphTracer";

/**
 * Trace the numeral the activity asks for (countingTraceNumeral → 0–9) on the
 * canvas tracer, with a row of that many pieces above the paper so the number
 * keeps its meaning. Completes when every stroke of the numeral passes.
 */
export default function TraceNumeralGame({ activity, speak, sfx, onComplete, onWrong, reduceMotion = false }) {
  const numeral = String(countingTraceNumeral(activity));
  const target = countingTarget(activity);
  const pieceCount = Math.min(10, target);
  const piece = activity?.number === 29 ? "bead" : "crayon";
  const noun = PIECE_NOUN[piece] || "item";
  const theme = COUNTING_ENGINE_THEME.traceNumeral || {};
  // The engine accent is a pale sky; ink needs contrast on white paper, so the
  // tracer draws in the theme's secondary (lavender) and keeps sky for chrome.
  const ink = theme.secondary || LEARN.color.lavender;
  const inkDeep = LEARN.color.lavenderDeep;
  const entry = GLYPH_STROKES[numeral];
  const strokes = useMemo(() => entry?.strokes || [], [entry]);
  const word = COUNTING_SPOKEN_WORDS[Number(numeral)] || numeral;
  const doneRef = useRef(false);

  // Say the numeral's formation hint once the player's prompt has been read.
  useEffect(() => {
    if (!entry?.hint) return undefined;
    const t = setTimeout(() => speak?.(entry.hint, { dedupe: true }), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeral]);

  const handleComplete = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    speak?.(word, { interrupt: true });
    onComplete?.();
  };

  // A row of stickers, 0.8 tiles each, shrunk so the whole set sits in one
  // row across the arena (never under 0.5 tiles: they are a picture, not a target).
  const gap = "calc(var(--tile) * 0.12)";
  const stickerSize = `max(calc(var(--tile) * 0.5), min(calc(var(--tile) * 0.8), calc((var(--arena-w, 760px) - calc(var(--tile) * 0.4) - ${gap} * ${pieceCount - 1}) / ${pieceCount})))`;

  return (
    <CountingRoot gap="calc(var(--tile) * 0.14)">
      <div
        role="img"
        aria-label={`${pieceCount} ${noun}${pieceCount === 1 ? "" : "s"}`}
        style={{ flexShrink: 0, display: "flex", justifyContent: "center", alignItems: "center", gap, flexWrap: "wrap", padding: "2px 0 calc(var(--tile) * 0.06)" }}
      >
        {Array.from({ length: pieceCount }, (_, i) => (
          <motion.span
            key={i}
            initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={reduceMotion ? { duration: 0 } : { ...POP_SPRING, delay: Math.min(i * 0.05, 0.5) }}
            style={{ display: "inline-flex" }}
          >
            <CountingPiece asset={piece} variant="sticker" size={stickerSize} phase={i} floats reduceMotion={reduceMotion} />
          </motion.span>
        ))}
      </div>
      <GlyphTracer
        key={numeral}
        glyph={numeral}
        strokes={strokes}
        label={`Trace numeral ${numeral}`}
        accent={ink}
        accentDeep={inkDeep}
        speak={speak}
        sfx={sfx}
        onWrong={onWrong}
        onComplete={handleComplete}
        reduceMotion={reduceMotion}
      />
    </CountingRoot>
  );
}
